// Taken from https://gist.github.com/rohanai/2587603f87ec8fbf943acb01e7f6a9f5
import { useEffect, useState } from 'react';

export type UseDynamicScriptArgs = {
  url: string;
  log: (...args: any) => void;
  error: (...args: any) => void;
};

type Loading = {
  setReady: React.Dispatch<React.SetStateAction<boolean>>;
  setFailed: React.Dispatch<React.SetStateAction<boolean>>;
};

type Loaded = {
  ready: boolean;
  failed: boolean;
};

export const loading: Record<string, Loading[]> = {};
export const loaded: Record<string, Loaded> = {};

export const useDynamicScript = ({ url, log, error }: UseDynamicScriptArgs) => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url) {
      return;
    }

    let element: any;

    if (loaded[url]) {
      log(`Dynamic Module Loaded Already: ${url}`);
      setReady(loaded[url].ready);
      setFailed(loaded[url].failed);
      return;
    }

    if (document.head.querySelector(`script[src='${url}']`)) {
      log(`Dynamic Module Loading: ${url}`);
      loading[url].push({ setReady, setFailed });
      return;
    }
    loading[url] = [{ setReady, setFailed }];

    element = document.createElement('script');
    element.src = url;
    element.type = 'text/javascript';
    element.async = true;

    setReady(false);
    setFailed(false);

    element.addEventListener('load', () => {
      log(`Dynamic Module Loaded: ${url}`);
      loading[url].forEach((u) => u.setReady(true));
      loaded[url] = { ready: true, failed: false };
    });

    element.addEventListener('error', () => {
      error(`Dynamic Module Error: ${url}`);
      loading[url].forEach((u) => u.setReady(false));
      loading[url].forEach((u) => u.setFailed(true));
      loaded[url] = { ready: false, failed: true };
    });

    document.head.append(element);
  }, [url]);

  return {
    ready,
    failed
  };
};
